import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import api from '../services/api';

const UserSettings = () => {
    const { user, setUser, logout } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        setName(user?.name || '');
        setEmail(user?.email || '');
    }, [user]);

    // Delete account flow
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteInput, setDeleteInput] = useState('');

    const handleSave = async () => {
        setSaveLoading(true);
        setError('');
        try {
            const { data } = await api.patch('/api/auth/me', {
                name: name.trim(),
                email: email.trim()
            });
            setUser(data.user);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (saveError) {
            setError(saveError?.response?.data?.error || 'Failed to save profile');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteInput === 'DELETE') {
            setDeleteLoading(true);
            setError('');
            try {
                await api.delete('/api/auth/me');
                await logout();
                navigate('/');
            } catch (deleteError) {
                setError(deleteError?.response?.data?.error || 'Failed to delete account');
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    return (
        <>
            <header className="page-header">
                <h1 className="page-title">Settings</h1>
            </header>

            {/* Save success toast */}
            {saveSuccess && (
                <div style={{
                    position: 'fixed', top: '24px', right: '24px', background: '#1A2235',
                    border: '1px solid #10B981', borderRadius: '8px', padding: '12px 20px',
                    color: '#10B981', fontSize: '13px', fontWeight: '600', zIndex: 1000,
                    display: 'flex', alignItems: 'center', gap: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                }}>
                    <i className="fas fa-check-circle"></i>
                    Profile updated
                </div>
            )}
            {error && (
                <div style={{
                    position: 'fixed', top: '24px', right: '24px', background: '#1A2235',
                    border: '1px solid #DC2626', borderRadius: '8px', padding: '12px 20px',
                    color: '#DC2626', fontSize: '13px', fontWeight: '600', zIndex: 1000
                }}>
                    {error}
                </div>
            )}

            <div className="settings-container">
                {/* Profile Section */}
                <div className="settings-card">
                    <h2 className="card-title">Profile</h2>
                    <div className="profile-row">
                        <div className="profile-avatar">{user?.initials || (name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?')}</div>
                        <div className="profile-inputs">
                            <div className="input-group">
                                <label className="input-label">Name</label>
                                <input type="text" className="input-field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Email</label>
                                <input type="email" className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" />
                            </div>
                        </div>
                    </div>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saveLoading} style={saveLoading ? { opacity: 0.7 } : {}}>
                        <i className="fas fa-check"></i>
                        {saveLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>

                {/* Short Domain Section */}
                <div className="settings-card">
                    <h2 className="card-title">Short Domain</h2>
                    <div className="input-group">
                        <label className="input-label">Your short domain</label>
                        <div className="input-wrapper">
                            <input type="text" className="input-field monospace" defaultValue="api.beakn.lol" disabled />
                            <i className="fas fa-lock input-icon"></i>
                        </div>
                        <div className="domain-info">
                            Custom domains available on the Business plan. <a href="#" className="upgrade-link">Upgrade</a>
                        </div>
                    </div>
                </div>

                {/* Danger Zone Section */}
                <div className="settings-card danger">
                    <h2 className="card-title danger">Danger Zone</h2>
                    <p className="warning-text">
                        Permanently deletes your account, all links, and analytics data. This action cannot be undone.
                    </p>
                    {!showDeleteConfirm ? (
                        <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                            <i className="fas fa-trash"></i>
                            Delete Account
                        </button>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <label style={{ fontSize: '13px', color: '#6B7280' }}>
                                Type <strong style={{ color: '#DC2626' }}>DELETE</strong> to confirm:
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="text"
                                    className="input-field"
                                    value={deleteInput}
                                    onChange={(e) => setDeleteInput(e.target.value)}
                                    placeholder="DELETE"
                                    style={{ flex: 1, maxWidth: '200px' }}
                                    autoFocus
                                />
                                <button 
                                    className="btn btn-danger" 
                                    onClick={handleDeleteAccount}
                                    disabled={deleteInput !== 'DELETE' || deleteLoading}
                                    style={deleteInput !== 'DELETE' || deleteLoading ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                >
                                    {deleteLoading ? 'Deleting...' : 'Confirm'}
                                </button>
                                <button className="btn btn-ghost" onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserSettings;
